// Generic LLM caller via the proxy
// Usage examples:
//   askLLM("user text")
//   askLLM({ systemInstruction, userText })
//   askLLM({ messages, model, temperature, top_p, max_tokens, stop })
//   askLLM({ userText, purpose })  // purpose tags a request for server-side capability checks, e.g. 'patent_packet'
//
// Automatically attaches the current user's Firebase ID token as a Bearer header
// so the proxy can verify the request is authenticated.

import { auth } from '../config/firebase';
import { capabilityDeniedMessage } from '../utils/capabilityErrors';

async function getAuthHeader() {
  try {
    const token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

async function askLLM(arg) {
  const opts = typeof arg === 'string' ? { userText: arg } : (arg || {});

  const {
    messages,
    systemInstruction,
    userText,
    model = process.env.REACT_APP_OPENAI_MODEL,
    temperature,
    top_p,
    max_tokens,
    stop,
    response_format,
    signal,
    purpose,
  } = opts;

  const finalMessages = Array.isArray(messages) && messages.length > 0
    ? messages
    : [
        ...(systemInstruction ? [{ role: 'system', content: systemInstruction }] : []),
        ...(userText ? [{ role: 'user', content: userText }] : []),
      ];

  if (!finalMessages || finalMessages.length === 0) {
    throw new Error('askLLM requires either messages or systemInstruction/userText');
  }

  const body = { model, messages: finalMessages };
  if (typeof temperature === 'number') body.temperature = temperature;
  if (typeof top_p === 'number') body.top_p = top_p;
  if (typeof max_tokens === 'number') body.max_tokens = max_tokens;
  if (stop !== undefined) body.stop = stop;
  if (response_format !== undefined) body.response_format = response_format;
  if (purpose) body.purpose = purpose;

  const authHeader = await getAuthHeader();
  const endpoint = process.env.REACT_APP_GPT_API || 'http://localhost:8020/api/chat';

  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok) {
    let errMsg = `Proxy error: ${resp.status}`;
    try {
      const errData = await resp.json();
      const denied = capabilityDeniedMessage(errData);
      if (denied) errMsg = denied;
      else if (errData.error) errMsg += `: ${errData.error}`;
    } catch (_) {}
    throw new Error(errMsg);
  }

  const data = await resp.json();
  return data.text;
}

export { askLLM };
